/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {EntityStatus} from '@ion/core/src/types/entities';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormRadioGroup} from '@ion/dls/src/components/forms/FormRadioGroup';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import DeleteServiceEntryButton, {DeleteServiceEntryProps} from './DeleteServiceEntry';
import {Entry} from './Services';

export interface MaintainServiceEntryProps extends DeleteServiceEntryProps {
  onUpdate?: (id: number, name: string, value: string) => void;
}

export interface ServiceEntryProps extends Entry, MaintainServiceEntryProps {
  level?: number;
  fullWidth?: boolean;
}

const useStyles = makeStyles(() => ({
  statusContainer : {
    paddingLeft : ({isPhone, fullWidth}: {isPhone: boolean, fullWidth: boolean}) => !isPhone && !fullWidth ? '60px' : '0px',
    paddingTop  : ({fullWidth}: {isPhone: boolean, fullWidth: boolean}) => fullWidth ? '60px' : '0px'
  }
}));

const STATUS_OPTIONS = [{
  label    : EntityStatus.Enabled,
  value    : EntityStatus.Enabled,
  disabled : false
},
{
  label    : EntityStatus.Disabled,
  value    : EntityStatus.Disabled,
  disabled : false
}];

/**
 * Handles displaying the given service entry as user friendly form fields.
 *
 * ```typescript
 *
 *  import ServiceEntry from './ServiceEntry';
 *
 *  <ServiceEntry id={id}
 *    service={service}
 *    level={level}
 *    onUpdate={onUpdate}
 *    onDelete={onDelete} />
 *
 * ```
 */
export const ServiceEntry = ({
  id,
  service,
  level,
  onUpdate,
  onDelete,
  fullWidth = false
}: ServiceEntryProps) => {
  const isPhone = useIsPhone();
  const classes = useStyles({isPhone, fullWidth});
  const {
    name,
    options,
    error,
    status
  } = service;

  const onServiceDropdownChange = useCallback(function(event): void {
    const {name, value} = event?.target;
    onUpdate(id, name, value);
  }, [id]);

  const onStatusChange = useCallback(function(value: string, name: string): void {
    onUpdate(id, name, value);
  }, [id]);

  return (
    <>
      <FormItem disableMinWidth fullWidth={fullWidth}
        customWidth={6}>
        <Dropdown required
          displaySelectedLabel
          options={options}
          fieldLabel={translate('admin-devices:services.manageServices.service.label')}
          name={'name'}
          key={`name_${id}`}
          value={name}
          hasError={error}
          errorText={translate(`admin-devices:services.manageServices.service.${name ? 'error' : 'placeholder'}`)}
          onChange={onServiceDropdownChange} />
      </FormItem>
      <FormItem disableMinWidth fullWidth={fullWidth}
        customWidth={5}>
        <div className={classes.statusContainer}>
          <FormRadioGroup displayAsRow
            options={STATUS_OPTIONS}
            name={'status'}
            key={`status_${id}`}
            groupLabel={translate('admin-devices:services.manageServices.serviceStatus')}
            onChange={onStatusChange}
            defaultValue={status} />
        </div>
      </FormItem>
      <DeleteServiceEntryButton level={level} id={id} onDelete={onDelete} />
    </>
  );
};

export default ServiceEntry;
